<template>
  <!-- 병원/기공소 추가·수정 모달 — SPEC-CHINA-ADMIN-HOSPITAL-001 REQ-FORM-001/002/003 -->
  <a-modal
    :open="open"
    :title="modalTitle"
    width="900px"
    :footer="null"
    :closable="true"
    :mask-closable="false"
    :keyboard="false"
    :centered="true"
    :destroy-on-close="true"
    :body-style="{ padding: 0 }"
    @cancel="onCancel"
  >
    <!-- 스크롤 영역 — 하단 버튼바를 상시 노출(sticky)하기 위해 폼 본문만 내부 스크롤시킨다 -->
    <div class="cfm-scroll-area">
      <a-form
        ref="formRef"
        :model="formData"
        layout="vertical"
        class="cfm-show-required"
      >
        <!-- 출고처 선택 패널 — SPEC-CHINA-COMPANY-OUTBOUND-PICKER-001 M3 (REQ-OP-030) -->
        <!-- 생성 모드 + 기공소 + 제조사(마스터·STAFF 동일)만 노출. 운영자·병원·편집 모드 미노출 -->
        <OutboundCustomerPicker
          v-if="showOutboundPicker"
          style="margin-bottom: 20px;"
          @select="onOutboundSelect"
        />

        <!-- ▌사업자 신원 -->
        <div class="cfm-section-header cfm-section-header--first">{{ $t('company_form_section_identity') }}</div>

        <!-- 회사명 -->
        <a-form-item :label="$t(`company_form_name${entitySuffix}`)" name="name"
          :rules="[{ required: true, message: $t(`company_form_name_placeholder${entitySuffix}`) }]"
        >
          <a-input v-model:value="formData.name" :placeholder="$t(`company_form_name_placeholder${entitySuffix}`)" />
          <!-- 출고처 provenance 칩 — REQ-OP-031: 이름 수정에도 유지, 清除는 칩만 제거 -->
          <div v-if="outboundProvenanceCode" class="outbound-provenance-chip">
            <span class="cn">来自出货记录</span>
            <b class="mono">{{ outboundProvenanceCode }}</b>
            <span>·</span>
            <a class="cn" @click="clearOutboundProvenance">清除</a>
          </div>
          <!-- 영업집조 OCR provenance 칩 — SPEC-CHINA-BIZLICENSE-OCR-001 M3: 값의 출처(사람 입력 vs OCR)를 구분 노출 -->
          <div v-if="ocrProvenanceFields.includes('name')" class="outbound-provenance-chip">
            <span class="cn">{{ $t('company_form_businessLicense_ocr_provenance') }}</span>
            <a class="cn" @click="clearOcrProvenance('name')">{{ $t('company_form_businessLicense_ocr_provenance_clear') }}</a>
          </div>
        </a-form-item>

        <!-- 법인대표 | 统一社会信用代码 — 2열 배치 -->
        <a-row :gutter="16">
          <a-col :xs="24" :sm="12">
            <!-- PII 필드: 법인대표 — 운영자(XINLAIAN)는 원문, 非운영자는 마스킹 표시 -->
            <a-form-item :label="$t('company_form_ceo')" name="ceo">
              <a-input v-model:value="formData.ceo" :placeholder="$t('company_form_ceo_placeholder')" />
              <div v-if="ocrProvenanceFields.includes('ceo')" class="outbound-provenance-chip">
                <span class="cn">{{ $t('company_form_businessLicense_ocr_provenance') }}</span>
                <a class="cn" @click="clearOcrProvenance('ceo')">{{ $t('company_form_businessLicense_ocr_provenance_clear') }}</a>
              </div>
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12">
            <!-- PII 필드: 통일사회신용코드 -->
            <a-form-item :label="$t('company_form_cNo')" name="cNo">
              <a-input v-model:value="formData.cNo" :placeholder="$t('company_form_cNo_placeholder')" />
              <div v-if="ocrProvenanceFields.includes('cNo')" class="outbound-provenance-chip">
                <span class="cn">{{ $t('company_form_businessLicense_ocr_provenance') }}</span>
                <a class="cn" @click="clearOcrProvenance('cNo')">{{ $t('company_form_businessLicense_ocr_provenance_clear') }}</a>
              </div>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- 영업집조(营业执照) 이미지 업로드 — SPEC-CHINA-COS-STORAGE-PROVIDER-001 M4 (REQ-CS-040~044) -->
        <!-- 영업집조 번호는 cNo(统一社会信用代码)와 동일하므로 별도 입력 안 받음 -->
        <!-- URL 텍스트 입력은 제거(REQ-CS-040/판단 14) — 업로드 위젯만 노출 -->
        <a-form-item
          :label="$t('company_form_businessLicenseImageUrl')"
          name="businessLicenseImageUrl"
          :tooltip="$t('company_form_businessLicense_ocr_tooltip')"
        >
          <!-- 상태 (c): 값 존재 — 미리보기 + 교체/제거 -->
          <div v-if="formData.businessLicenseImageUrl" class="business-license-widget">
            <a-image
              :src="formData.businessLicenseImageUrl"
              :width="120"
              style="border: 1px solid #eee; border-radius: 4px;"
              :preview="{ mask: $t('company_form_businessLicense_preview') }"
            />
            <div class="business-license-actions">
              <a-upload
                accept=".jpg,.jpeg,.png,.webp"
                :show-upload-list="false"
                :custom-request="onUploadBusinessLicense"
                :before-upload="beforeUploadBusinessLicense"
              >
                <a-button size="small" :loading="businessLicenseUploading">
                  {{ $t('company_form_businessLicense_replace_btn') }}
                </a-button>
              </a-upload>
              <!-- 제거 버튼: 판단 18/C-2 — create는 항상, edit은 이번 세션 신규 업로드일 때만 노출 -->
              <a-button
                v-if="showBusinessLicenseRemove"
                size="small"
                danger
                @click="onRemoveBusinessLicense"
              >
                {{ $t('company_form_businessLicense_remove_btn') }}
              </a-button>
            </div>
          </div>
          <!-- 상태 (a)/(b): 값 없음 — 업로드 버튼 + 힌트 (업로드 중엔 :loading만 반영, Save는 계속 가능) -->
          <div v-else>
            <a-upload
              accept=".jpg,.jpeg,.png,.webp"
              :show-upload-list="false"
              :custom-request="onUploadBusinessLicense"
              :before-upload="beforeUploadBusinessLicense"
            >
              <a-button :loading="businessLicenseUploading">
                {{ $t('company_form_businessLicense_upload_btn') }}
              </a-button>
            </a-upload>
            <div class="business-license-hint">{{ $t('company_form_businessLicense_upload_hint') }}</div>
          </div>
          <!-- 상태 (d): 실패 — 위젯 범위 에러만 표시, 폼 본체/저장은 영향 없음 -->
          <a-alert
            v-if="businessLicenseError"
            type="error"
            :message="businessLicenseError"
            show-icon
            closable
            style="margin-top: 8px;"
            @close="businessLicenseError = ''"
          />

          <!-- 영업집조 OCR 자동 채움 — SPEC-CHINA-BIZLICENSE-OCR-001 M3. 업로드 성공 직후 자동 1회 실행.
               이미지가 있으면 실행 링크를 항상 노출한다 — 수정 모달을 다시 열면 ocrAttempted가 리셋되므로
               (이미 저장된 영업집조에는 이번 세션의 자동 실행이 없다) 링크를 ocrAttempted로 가두면
               "이미지는 있는데 인식만 다시 돌릴 방법이 없는" 상태가 된다. 라벨만 상태에 따라 바꾼다. -->
          <div v-if="formData.businessLicenseImageUrl" class="business-license-ocr-area">
            <div v-if="ocrExtracting" class="business-license-ocr-status">
              <a-spin size="small" />
              <span>{{ $t('company_form_businessLicense_ocr_extracting') }}</span>
            </div>
            <a
              v-else
              class="business-license-ocr-retry"
              @click="retryBusinessLicenseOcr"
            >
              {{
                ocrAttempted
                  ? $t('company_form_businessLicense_ocr_retry_btn')
                  : $t('company_form_businessLicense_ocr_run_btn')
              }}
            </a>

            <!-- OCR 실패 — 위젯 범위 경고만, 폼 본체/저장은 영향 없음 -->
            <a-alert
              v-if="ocrError"
              type="warning"
              :message="ocrError"
              show-icon
              closable
              style="margin-top: 8px;"
              @close="ocrError = ''"
            />

            <!-- 식별 결과 제안 패널 — 사람이 확인 후 선택 적용(자동 반영 금지) -->
            <div v-if="ocrProposal" class="business-license-ocr-proposal">
              <div class="business-license-ocr-proposal__title">
                {{ $t('company_form_businessLicense_ocr_proposal_title') }}
              </div>

              <!-- cNo는 체크섬 실패 시 절대 값을 내려주지 않는다 — 총체적 실패가 아니라 "직접 입력 필요"로 안내 -->
              <a-alert
                v-if="ocrProposal.warnings.includes('BLO-CNO-001')"
                type="info"
                :message="$t('company_form_businessLicense_ocr_cno_unreliable')"
                show-icon
                style="margin-bottom: 8px;"
              />

              <template v-if="ocrProposalFieldList.length">
                <div
                  v-for="item in ocrProposalFieldList"
                  :key="item.key"
                  class="business-license-ocr-field"
                >
                  <a-checkbox v-model:checked="ocrSelected[item.key]">
                    <span class="business-license-ocr-field__label">{{ item.label }}</span>
                    <span class="business-license-ocr-field__value">{{ item.value }}</span>
                  </a-checkbox>
                </div>
              </template>
              <div v-else class="business-license-hint">
                {{ $t('company_form_businessLicense_ocr_no_fields') }}
              </div>

              <div class="business-license-ocr-proposal__actions">
                <a-button size="small" @click="dismissOcrProposal">
                  {{ $t('company_form_businessLicense_ocr_dismiss_btn') }}
                </a-button>
                <a-button
                  size="small"
                  type="primary"
                  :disabled="!hasOcrSelection"
                  @click="applyOcrSelection"
                >
                  {{ $t('company_form_businessLicense_ocr_apply_btn') }}
                </a-button>
              </div>
            </div>
          </div>
        </a-form-item>

        <!-- ▌소재지 -->
        <div class="cfm-section-header">{{ $t('company_form_section_address') }}</div>

        <!-- 省 | 市 | 区 — 행정 층위 3열. 직할시는 省·市에 같은 값이 들어간다 -->
        <a-row :gutter="16">
          <a-col :xs="24" :sm="8">
            <!-- 주소 1급: addrOne (省/直辖市) -->
            <a-form-item :label="$t('company_form_addrOne')" name="addrOne">
              <a-input v-model:value="formData.addrOne" :placeholder="$t('company_form_addrOne_placeholder')" />
              <div v-if="ocrProvenanceFields.includes('addrOne')" class="outbound-provenance-chip">
                <span class="cn">{{ $t('company_form_businessLicense_ocr_provenance') }}</span>
                <a class="cn" @click="clearOcrProvenance('addrOne')">{{ $t('company_form_businessLicense_ocr_provenance_clear') }}</a>
              </div>
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="8">
            <!-- 주소 2급: addrTwo (市) -->
            <a-form-item :label="$t('company_form_addrTwo')" name="addrTwo">
              <a-input v-model:value="formData.addrTwo" :placeholder="$t('company_form_addrTwo_placeholder')" />
              <div v-if="ocrProvenanceFields.includes('addrTwo')" class="outbound-provenance-chip">
                <span class="cn">{{ $t('company_form_businessLicense_ocr_provenance') }}</span>
                <a class="cn" @click="clearOcrProvenance('addrTwo')">{{ $t('company_form_businessLicense_ocr_provenance_clear') }}</a>
              </div>
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="8">
            <!-- 주소 3급: addrThree (区/县) -->
            <a-form-item :label="$t('company_form_addrThree')" name="addrThree">
              <a-input v-model:value="formData.addrThree" :placeholder="$t('company_form_addrThree_placeholder')" />
              <div v-if="ocrProvenanceFields.includes('addrThree')" class="outbound-provenance-chip">
                <span class="cn">{{ $t('company_form_businessLicense_ocr_provenance') }}</span>
                <a class="cn" @click="clearOcrProvenance('addrThree')">{{ $t('company_form_businessLicense_ocr_provenance_clear') }}</a>
              </div>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- PII 필드: 상세 주소 -->
        <a-form-item :label="$t('company_form_addrDetail')" name="addrDetail">
          <a-input v-model:value="formData.addrDetail" :placeholder="$t('company_form_addrDetail_placeholder')" />
          <div v-if="ocrProvenanceFields.includes('addrDetail')" class="outbound-provenance-chip">
            <span class="cn">{{ $t('company_form_businessLicense_ocr_provenance') }}</span>
            <a class="cn" @click="clearOcrProvenance('addrDetail')">{{ $t('company_form_businessLicense_ocr_provenance_clear') }}</a>
          </div>
        </a-form-item>

        <!-- ▌운영 정보 -->
        <div class="cfm-section-header">{{ $t('company_form_section_operation') }}</div>

        <!-- 진료과 (선택지 + 자유 입력 tags, 코드 저장/로케일 라벨 표시) -->
        <a-form-item :label="$t('company_form_medicalDept')" name="medicalDept">
          <a-select
            v-model:value="formData.medicalDept"
            mode="tags"
            :placeholder="$t('company_form_medicalDept_placeholder')"
            style="width: 100%;"
          >
            <a-select-option v-for="code in deptCodes" :key="code" :value="code">
              {{ $t(`company_dept_${code}`) }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <!-- 등록 상태 | 전화번호 — 2열 배치 -->
        <a-row :gutter="16">
          <a-col :xs="24" :sm="12">
            <!-- 등록 상태 -->
            <a-form-item :label="$t('company_form_registrationStatus')" name="registrationStatus">
              <a-select
                v-model:value="formData.registrationStatus"
                :placeholder="$t('company_form_registrationStatus_placeholder')"
                allow-clear
              >
                <a-select-option value="ACTIVE">{{ $t('company_status_ACTIVE') }}</a-select-option>
                <a-select-option value="PENDING">{{ $t('company_status_PENDING') }}</a-select-option>
                <a-select-option value="APPROVED">{{ $t('company_status_APPROVED') }}</a-select-option>
                <a-select-option value="REJECTED">{{ $t('company_status_REJECTED') }}</a-select-option>
                <a-select-option value="SUSPENDED">{{ $t('company_status_SUSPENDED') }}</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12">
            <!-- 전화번호 -->
            <a-form-item :label="$t('company_form_call')" name="call">
              <a-input v-model:value="formData.call" :placeholder="$t('company_form_call_placeholder')" />
            </a-form-item>
          </a-col>
        </a-row>

        <!-- 테스트 회사 여부 — label과 switch를 한 줄에 배치(REQ: inline switch) -->
        <a-form-item name="isTestCompany">
          <div class="cfm-inline-field">
            <span class="cfm-inline-label">{{ $t('company_form_isTestCompany') }}</span>
            <a-switch v-model:checked="formData.isTestCompany" />
          </div>
        </a-form-item>

        <!-- ▌연동 · 권한 — XINLAIAN(운영자)만 노출. 헝지우 종속 블록 + authz 필드가 모두 이 섹션의 자식 -->
        <!-- [주의] isOperator 단독으로 걸면 자식이 하나도 없는 조합에서 헤더만 남는다.
             예: 병원 생성(헝지우=기공소 전용이라 없음 + authz=생성모드 미노출) → 빈 섹션.
             자식 존재 여부를 함께 판정한다(showIntegrationSection). -->
        <template v-if="showIntegrationSection">
          <div class="cfm-section-header">{{ $t('company_form_section_integration') }}</div>

          <!-- 헝지우(恒久) 연동 대리등록 — 기공소 전용 종속 자식 -->
          <!-- SPEC-CHINA-MFR-LAB-WRITE-HENGJIU-PROXY-001: 운영자 + 제조사(대리) 노출.
               입력은 항상 선택(REQ-HP-001)이며, 저장 시 즉시 활성이 아니라 PENDING 클레임만 생성된다.
               활성화는 헝지우 서버의 실제 서명 검증이 판정한다 — 대리자는 소유자가 아니다. -->
          <template v-if="type === 'DENTAL_LABORATORY'">
            <a-form-item name="hengjiuEnabled">
              <div class="cfm-inline-field">
                <span class="cfm-inline-label">{{ $t('company_form_hengjiu_section') }}</span>
                <a-switch v-model:checked="formData.hengjiuEnabled" />
              </div>
              <div class="cfm-inline-hint">{{ $t('company_form_hengjiu_proxy_hint') }}</div>
            </a-form-item>

            <template v-if="formData.hengjiuEnabled">
              <!-- 이미 연동된 기공소는 대리자가 값을 덮어쓸 수 없다 — 현재 상태만 읽기전용으로 보여준다 -->
              <a-alert
                v-if="isEdit && record && record.hengjiuFactoryId"
                type="success"
                show-icon
                style="margin-bottom: 12px;"
                :message="$t('company_form_hengjiu_already_linked', { factoryId: record.hengjiuFactoryId })"
              />

              <template v-else>
                <!-- REQ-HP-006: 대상 기공소의 현재 연동 상태. pending 을 감추면 대리자가
                     이미 제출된 값을 모른 채 중복 입력한다. -->
                <a-alert
                  v-if="hengjiuStatusLoading"
                  type="info"
                  show-icon
                  style="margin-bottom: 12px;"
                  :message="$t('company_form_hengjiu_status_loading')"
                />
                <a-alert
                  v-else-if="hengjiuStatus && hengjiuStatus.state === 'pending'"
                  type="warning"
                  show-icon
                  style="margin-bottom: 12px;"
                  :message="$t('company_form_hengjiu_status_pending', { factoryId: hengjiuStatus.factoryId || '-' })"
                  :description="hengjiuStatus.signatureFailCount
                    ? $t('company_form_hengjiu_status_pending_fail', { count: hengjiuStatus.signatureFailCount })
                    : ''"
                />

                <!-- REQ-HP-001: 선택 입력 — required 규칙 없음 -->
                <a-form-item :label="$t('company_form_hengjiu_factoryId')" name="hengjiuFactoryId">
                  <a-input
                    v-model:value="formData.hengjiuFactoryId"
                    :placeholder="$t('company_form_hengjiu_factoryId_placeholder')"
                    style="font-family: monospace;"
                  />
                  <div style="font-size: 11px; color: #888; margin-top: 4px;">
                    {{ hengjiuStatus && hengjiuStatus.state === 'pending'
                      ? $t('company_form_hengjiu_resubmit_hint')
                      : $t('company_form_hengjiu_factoryId_optional_hint') }}
                  </div>
                </a-form-item>

              </template>
            </template>
          </template>

          <!-- authz 필드: 운영자(XINLAIAN)만 편집 가능 (REQ-FORM-003, REQ-STATE-002) -->
          <!-- C안: create는 미노출(payload도 무시), edit+값 있을 때만 읽기전용 목록 + [변경] 버튼으로 노출 -->
          <a-form-item
            v-if="showManufacturerIdsField"
            :label="$t('company_form_viewManufacturerIds')"
            name="viewManufacturerIds"
          >
            <div v-if="!manufacturerIdsRevealed" class="authz-readonly-row">
              <span v-if="optionsLoading" class="authz-loading-text">
                {{ $t('company_form_authz_loading') }}
              </span>
              <span v-else class="authz-readonly-tags">
                <a-tag
                  v-for="item in manufacturerDisplayItems"
                  :key="item.id"
                  :class="{ 'authz-unknown-tag': !item.label }"
                >
                  {{ item.label || $t('company_form_authz_unknown_company', { id: item.id.slice(0, 8) }) }}
                </a-tag>
              </span>
              <a-button size="small" type="link" @click="manufacturerIdsRevealed = true">
                {{ $t('company_form_authz_change_btn') }}
              </a-button>
            </div>
            <a-select
              v-else
              v-model:value="formData.viewManufacturerIds"
              mode="multiple"
              show-search
              option-filter-prop="label"
              :options="manufacturerOptions"
              :loading="optionsLoading"
              :placeholder="$t('company_form_viewManufacturerIds_placeholder')"
              style="width: 100%;"
            />
          </a-form-item>

          <a-form-item
            v-if="showDentalLabIdsField"
            :label="$t('company_form_viewDentalLabIds')"
            name="viewDentalLabIds"
          >
            <div v-if="!dentalLabIdsRevealed" class="authz-readonly-row">
              <span v-if="optionsLoading" class="authz-loading-text">
                {{ $t('company_form_authz_loading') }}
              </span>
              <span v-else class="authz-readonly-tags">
                <a-tag
                  v-for="item in dentalLabDisplayItems"
                  :key="item.id"
                  :class="{ 'authz-unknown-tag': !item.label }"
                >
                  {{ item.label || $t('company_form_authz_unknown_company', { id: item.id.slice(0, 8) }) }}
                </a-tag>
              </span>
              <a-button size="small" type="link" @click="dentalLabIdsRevealed = true">
                {{ $t('company_form_authz_change_btn') }}
              </a-button>
            </div>
            <a-select
              v-else
              v-model:value="formData.viewDentalLabIds"
              mode="multiple"
              show-search
              option-filter-prop="label"
              :options="dentalLabOptions"
              :loading="optionsLoading"
              :placeholder="$t('company_form_viewDentalLabIds_placeholder')"
              style="width: 100%;"
            />
          </a-form-item>
        </template>
      </a-form>
    </div>

    <!-- 하단 고정 영역 — 에러 표시 + 버튼바. 스크롤 영역 밖에 둬 항상 노출(sticky) -->
    <div class="cfm-fixed-bottom">
      <!-- 에러 표시 -->
      <a-alert
        v-if="errorMsg"
        type="error"
        :message="errorMsg"
        style="margin-bottom: 12px;"
        show-icon
        closable
        @close="errorMsg = ''"
      />

      <!-- 하단 버튼 -->
      <div style="display: flex; justify-content: flex-end; gap: 8px;">
        <a-button @click="onCancel">{{ $t('company_form_btn_cancel') }}</a-button>
        <a-button type="primary" :loading="submitting" @click="onSubmit">
          {{ isEdit ? $t('company_form_btn_save') : $t('company_form_btn_create') }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { notification } from 'ant-design-vue';
import {
  createCompany,
  updateCompany,
  getCompanyOptions,
  extractApiError,
} from '@/apis/chinaCompany';
// 영업집조 업로드 + OCR 자동채움은 자기 프로필 화면(CompanyProfile.vue)과 공유한다 — 규칙 단일 출처
import { businessLicenseOcrMixin } from '@/mixins/businessLicenseOcrMixin';
// 헝지우 대리등록은 즉시 ACTIVE 생성이 아니라 PENDING 클레임 — SPEC-CHINA-MFR-LAB-WRITE-HENGJIU-PROXY-001
import { createOnboardingClaim, getOnboardingStatus } from '@/apis/labOrdersOnboarding';
// @MX:NOTE: [AUTO] jwtRole/isOperator 헬퍼를 통한 actor role 판별 (UI 표시용, EXC-6 보안 강제는 백엔드)
import { normalizeTestFlag } from '@/utils/companyFlags';
import { jwtRole, isOperator as isOperatorRole } from '@/utils/utils';
import { extractErrorCodeToken } from '@/utils/outboundPickerError';
import OutboundCustomerPicker from './OutboundCustomerPicker.vue';

export default {
  name: 'CompanyFormModal',

  components: {
    OutboundCustomerPicker,
  },

  mixins: [businessLicenseOcrMixin],

  props: {
    // @MX:NOTE: [AUTO] REQ-UNW-006 — class/style을 props로 정의하지 않음 (fallthrough attribute 차단 방지)
    open: {
      type: Boolean,
      default: false,
    },
    /** HOSPITAL | DENTAL_LABORATORY */
    type: {
      type: String,
      required: true,
    },
    /** 수정 시 기존 회사 데이터, null이면 추가 모드 */
    record: {
      type: Object,
      default: null,
    },
  },

  emits: ['close', 'saved'],

  data() {
    return {
      submitting: false,
      errorMsg: '',
      formData: this.initFormData(),
      // 진료과 선택지(코드값 저장, 로케일 라벨 표시). tags 모드라 자유입력도 허용
      deptCodes: ['DENT', 'COMMON', 'DERM', 'OPH', 'PS', 'OS', 'VET_COMMON', 'KM'],
      // authz 필드(제조사/기공소) 이름 선택용 옵션 — 운영자(XINLAIAN)만 로드
      manufacturerOptions: [],
      dentalLabOptions: [],
      optionsLoading: false,
      // 출고처 선택 provenance — SPEC-CHINA-COMPANY-OUTBOUND-PICKER-001 REQ-OP-031
      // formData가 아니라 별도 필드로 관리: 화면 표시(칩)·payload 조립(create-only) 양쪽에서만 쓰이고
      // 서버가 결정적으로 파생하는 sourceOutboundDealerCode는 FE가 보유하지 않는다(REQ-OP-021).
      outboundProvenanceCode: null,
      // 영업집조 이미지/OCR 상태는 businessLicenseOcrMixin이 소유한다.
      // persistedBusinessLicenseImageUrl만 여기서 덮어쓴다 — mixin 기본값은 ''이지만 이 모달은
      // record를 prop으로 받아 마운트 시점에 이미 저장값을 알 수 있기 때문(판단 18/C-2:
      // edit에서 신규 업로드 후 제거 시 이 값으로 복원해 "빈 미리보기 → 저장 후 옛 이미지 부활"
      // 착시를 막는다). 아래 open/record watcher가 재오픈 때마다 다시 채운다.
      persistedBusinessLicenseImageUrl: this.record?.businessLicenseImageUrl || '',
      // authz 필드(제조사/기공소) 읽기전용→편집 토글 — 필드별 독립, 모달 open/close마다 초기화(SPEC 판단)
      manufacturerIdsRevealed: false,
      dentalLabIdsRevealed: false,
      // 대상 기공소의 헝지우 연동 상태 (REQ-HP-006) — 수정 화면에서만 조회
      hengjiuStatus: null,
      hengjiuStatusLoading: false,
    };
  },

  computed: {
    /** 화면 전반 용어 통일: HOSPITAL=병원(医院) / DENTAL_LABORATORY=기공소(技工所) — 门诊 등 파편 표기 금지 */
    entitySuffix() {
      return this.type === 'DENTAL_LABORATORY' ? '_dental_lab' : '_hospital';
    },
    modalTitle() {
      return this.$t(`${this.isEdit ? 'company_form_title_edit' : 'company_form_title_add'}${this.entitySuffix}`);
    },
    isEdit() {
      return !!this.record;
    },
    // @MX:NOTE: [AUTO] actor role은 JWT payload.role에서 읽음 — OD-FE-2 확정
    // REQ-UNW-007: store getter 복사 금지 — isOperator() 직접 호출(XINLAIAN 및 레거시 CERTILIFE 둘 다 허용)
    isOperator() {
      return isOperatorRole();
    },
    // CompanyList.vue:437 isManufacturer와 동일식 — store getter 복사 금지(REQ-UNW-007) 준용해 신설
    isManufacturer() {
      return jwtRole() === 'MANUFACTURER';
    },
    // REQ-OP-030: 생성 모드 + 기공소 + 제조사(마스터·STAFF 동일 취급)만 노출. 편집·병원·운영자 미노출
    showOutboundPicker() {
      return !this.isEdit && this.type === 'DENTAL_LABORATORY' && this.isManufacturer;
    },
    // isBusinessLicenseModified / showBusinessLicenseRemove 는 businessLicenseOcrMixin이 소유.
    // C안: create는 항상 미노출(payload도 이미 무시), edit이라도 값이 0개면 미노출
    /** 연동·권한 섹션 노출 — 자식이 최소 1개 있을 때만. 빈 헤더 방지 */
    showIntegrationSection() {
      // PROXY-001 REQ-UI-003: 헝지우 대리등록을 위해 제조사에게도 개방.
      // 헝지우 외 자식(authz 필드)은 여전히 운영자 전용이므로, 제조사에게는 기공소일 때만 의미가 있다.
      if (!this.isOperator) {
        return this.isManufacturer && this.type === 'DENTAL_LABORATORY';
      }
      // 헝지우 블록은 기공소에서 항상(토글 OFF여도 스위치 행이 남는다)
      if (this.type === 'DENTAL_LABORATORY') return true;
      return this.showManufacturerIdsField || this.showDentalLabIdsField;
    },
    showManufacturerIdsField() {
      return this.isEdit && this.formData.viewManufacturerIds.length > 0;
    },
    showDentalLabIdsField() {
      return this.isEdit && this.type === 'HOSPITAL' && this.formData.viewDentalLabIds.length > 0;
    },
    // 저장된 id를 옵션 목록에서 이름으로 해석 — 옵션에 없으면 label:null(댕글링 참조, 화면에서 물음표 처리)
    manufacturerDisplayItems() {
      return this.buildAuthzDisplayItems(
        this.formData.viewManufacturerIds,
        this.manufacturerOptions,
        this.record?.viewManufacturers,
      );
    },
    dentalLabDisplayItems() {
      return this.buildAuthzDisplayItems(
        this.formData.viewDentalLabIds,
        this.dentalLabOptions,
        this.record?.viewDentalLabs,
      );
    },
    /**
     * mixin의 ocrFieldLabels 오버라이드 — 이 모달만 회사명 라벨에 병원/기공소 접미사가 붙는다.
     * 나머지 라벨은 mixin 기본값과 동일하지만, computed는 부분 병합이 아니라 통째로 덮이므로
     * 전체를 다시 준다.
     */
    ocrFieldLabels() {
      return {
        name: this.$t(`company_form_name${this.entitySuffix}`),
        ceo: this.$t('company_form_ceo'),
        cNo: this.$t('company_form_cNo'),
        addrOne: this.$t('company_form_addrOne'),
        addrTwo: this.$t('company_form_addrTwo'),
        addrThree: this.$t('company_form_addrThree'),
        addrDetail: this.$t('company_form_addrDetail'),
      };
    },
  },

  watch: {
    // 모달이 열릴 때마다 폼 초기화
    open(val) {
      if (val) {
        this.formData = this.initFormData();
        this.errorMsg = '';
        this.outboundProvenanceCode = null;
        this.persistedBusinessLicenseImageUrl = this.record?.businessLicenseImageUrl || '';
        this.businessLicenseUploading = false;
        this.businessLicenseError = '';
        this.resetOcrState();
        // authz 필드 읽기전용→편집 토글도 모달 열 때마다 초기화 — 이전 세션에 열어둔 상태가 새지 않게
        this.manufacturerIdsRevealed = false;
        this.dentalLabIdsRevealed = false;
        this.loadAuthzOptions();
        this.loadHengjiuStatus();
      }
    },
    record(val) {
      if (val) {
        this.formData = this.initFormData();
        this.persistedBusinessLicenseImageUrl = val?.businessLicenseImageUrl || '';
      }
    },
  },

  methods: {
    /**
     * 대상 기공소의 헝지우 연동 상태 조회 (REQ-HP-006).
     *
     * 수정 화면 + 기공소일 때만 의미가 있다. 생성 화면에는 대상 회사가 아직 없다.
     * 실패해도 폼은 그대로 동작한다 — 상태 표시는 보조 정보이지 저장 경로가 아니다.
     */
    async loadHengjiuStatus() {
      this.hengjiuStatus = null;
      const companyId = this.record?._id;
      if (!this.isEdit || this.type !== 'DENTAL_LABORATORY' || !companyId) return;
      // 이미 연동된 기공소는 record.hengjiuFactoryId 로 화면이 성립하므로 호출하지 않는다.
      if (this.record?.hengjiuFactoryId) return;

      this.hengjiuStatusLoading = true;
      try {
        this.hengjiuStatus = await getOnboardingStatus({ companyId });
        // pending 은 아직 회사에 hengjiuFactoryId 가 박히기 전이라 스위치가 꺼진 채로 열린다.
        // 그러면 섹션이 접혀 경고가 보이지 않고, 대리자는 제출 사실을 모른 채 중복 입력한다.
        if (this.hengjiuStatus?.state === 'pending') {
          this.formData.hengjiuEnabled = true;
        }
      } catch (e) {
        this.hengjiuStatus = null;
      } finally {
        this.hengjiuStatusLoading = false;
      }
    },

    // authz 필드 옵션 로드 — 운영자(XINLAIAN)만. 실패해도 폼은 동작(빈 옵션)
    async loadAuthzOptions() {
      if (!this.isOperator) return;
      this.optionsLoading = true;
      try {
        // 2026-09-02 C안: viewManufacturerIds 에는 딜러(총판) 회사 id 도 들어간다 — 딜러 계정은
        // role=MANUFACTURER 라 이 배열로 제휴 병원·기공소를 판정한다. BE 가 제조사 명단(type=MANUFACTURER)에
        // 딜러를 섞지 않으므로 여기서 type=DEALER 를 따로 받아 라벨에 표시하고 합친다.
        const tasks = [getCompanyOptions('MANUFACTURER'), getCompanyOptions('DEALER')];
        if (this.type === 'HOSPITAL') tasks.push(getCompanyOptions('DENTAL_LABORATORY'));
        const [mfrRes, dealerRes, labRes] = await Promise.all(tasks);
        const dealerSuffix = ` (${this.$t('company_form_option_dealer_suffix')})`;
        this.manufacturerOptions = [
          ...(mfrRes.data || []).map((o) => ({ value: o._id, label: o.name })),
          ...(dealerRes.data || []).map((o) => ({ value: o._id, label: `${o.name}${dealerSuffix}` })),
        ];
        this.dentalLabOptions = labRes ? (labRes.data || []).map((o) => ({ value: o._id, label: o.name })) : [];
      } catch (e) {
        // 옵션 로드 실패는 무시 — 빈 옵션으로 폼은 계속 동작
        this.manufacturerOptions = [];
        this.dentalLabOptions = [];
      } finally {
        this.optionsLoading = false;
      }
    },

    // 저장된 authz id 배열 → 화면 표시용 {id, label} 배열. 어디서도 못 찾은 id 는 label:null(댕글링 참조).
    //
    // 옵션 목록(company-options)은 운영자 전용이다 — 전 제조사 명단이 실려 있어 제조사에게 열 수 없다.
    // 그래서 제조사 계정에서는 옵션이 비어 전부 "알 수 없는 회사"로 떨어졌다.
    // 목록 API 가 이미 권한 범위에 맞게 이름을 붙여 내려주므로(record.viewManufacturers),
    // 그 값을 먼저 쓰고 옵션은 보조로 둔다.
    buildAuthzDisplayItems(ids, options, resolved) {
      const byId = new Map((resolved || []).map((r) => [r._id, r.name]));
      return (ids || []).map((id) => {
        const fromRecord = byId.get(id);
        if (fromRecord) return { id, label: fromRecord };
        const opt = options.find((o) => o.value === id);
        return { id, label: opt ? opt.label : null };
      });
    },

    initFormData() {
      const r = this.record || {};
      return {
        name: r.name || '',
        ceo: r.ceo || '',
        cNo: r.cNo || '',
        businessLicenseImageUrl: r.businessLicenseImageUrl || '',
        addrOne: r.addrOne || '',
        addrTwo: r.addrTwo || '',
        addrThree: r.addrThree || '',
        addrDetail: r.addrDetail || '',
        medicalDept: Array.isArray(r.medicalDept) ? [...r.medicalDept] : [],
        registrationStatus: r.registrationStatus || '',
        // SPEC-CHINA-TESTFLAG-NORMALIZE-001 REQ-TF-001: 4개 표현형(true/'Y'/'N'/부재)을 boolean으로 정규화.
        // `|| false` 로는 부족하다 — 문자열 'N' 도 truthy 라 그대로 통과하고, a-switch 는
        // `checked === checkedValue(true)` 엄격 비교라 문자열을 전부 OFF 로 그린다.
        // 결과적으로 테스트 회사('Y')가 "테스트 아님"으로 보이고, 저장하면 그 값이 재영속된다.
        isTestCompany: normalizeTestFlag(r.isTestCompany),
        call: r.call || '',
        // 헝지우 종속 정보 (DENTAL_LABORATORY 전용)
        hengjiuEnabled: !!r.hengjiuFactoryId,
        hengjiuFactoryId: r.hengjiuFactoryId || '',
        hengjiuFactoryName: '',
        hengjiuActive: true,
        // authz 필드 — 운영자(XINLAIAN)만 편집 (이외 role은 UI 자체 미노출)
        viewManufacturerIds: Array.isArray(r.viewManufacturerIds)
          ? [...r.viewManufacturerIds]
          : [],
        viewDentalLabIds: Array.isArray(r.viewDentalLabIds)
          ? [...r.viewDentalLabIds]
          : [],
      };
    },


    formatRotatedAt(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return String(iso);
      }
    },
    // 영업집조 업로드/OCR 자동채움 메서드 전부 businessLicenseOcrMixin이 소유한다
    // (beforeUploadBusinessLicense / onUploadBusinessLicense / mapBusinessLicenseUploadError /
    //  onRemoveBusinessLicense / resetOcrState / runBusinessLicenseOcr / retryBusinessLicenseOcr /
    //  applyOcrSelection / dismissOcrProposal / clearOcrProvenance / mapOcrError).


    onCancel() {
      this.$emit('close');
    },

    async onSubmit() {
      try {
        await this.$refs.formRef.validate();
      } catch {
        return;
      }

      this.submitting = true;
      this.errorMsg = '';

      try {
        // @MX:NOTE: [AUTO] REQ-FORM-001: create 시 authz 필드 미전송 (非운영자)
        // @MX:NOTE: [AUTO] REQ-FORM-003: 운영자(XINLAIAN)는 authz 필드 PATCH 가능
        const payload = this.buildPayload();

        // 출고처 선택 provenance — REQ-OP-020: create dto에만 실어 서버 검증
        const provenanceCode = this.outboundProvenanceCode;

        let createdRecord = null;
        if (this.isEdit) {
          await updateCompany(this.type, this.record._id, payload);
        } else {
          const res = await createCompany(this.type, payload);
          // 생성 응답에서 레코드 추출 (호출처 자동선택용, 응답 shape 불확실해 best-effort)
          createdRecord = res?.data?.data ?? res?.data ?? null;
          // REQ-OP-039: provenance를 실어 보냈다면 성공 응답의 에코를 검증
          if (provenanceCode) {
            this.verifyOutboundProvenanceEcho(provenanceCode, createdRecord);
          }
        }

        // 헝지우 대리등록 — 기공소 전용, 항상 선택 입력(REQ-HP-001).
        // REQ-HP-004: 회사 저장은 이미 성공한 상태다. 클레임이 실패해도 회사를 되돌리지 않고,
        // "회사는 저장됐고 헝지우 연동만 실패"임을 분명히 알려 수정 화면에서 재시도하게 한다.
        const targetCompanyId = this.isEdit
          ? this.record._id
          : createdRecord?._id ?? createdRecord?.id ?? null;
        let hengjiuOutcome = 'skipped';
        try {
          hengjiuOutcome = await this.persistHengjiuCredential(targetCompanyId);
        } catch (hengjiuErr) {
          const st = hengjiuErr?.response?.status;
          const detail =
            st === 409
              ? this.$t('company_error_hengjiu_conflict')
              : hengjiuErr?.response?.data?.message || hengjiuErr?.message || '';
          notification.warning({
            message: this.$t('company_hengjiu_partial_failure_title'),
            description: `${this.$t('company_hengjiu_partial_failure_desc')} ${detail}`,
            duration: 8,
          });
        }

        // REQ-UI-007: 대리등록은 "등록 완료"가 아니라 "연결 테스트 후 자동 활성화"로 안내한다.
        if (hengjiuOutcome === 'claimed') {
          notification.info({
            message: this.$t('company_hengjiu_claim_submitted_title'),
            description: this.$t('company_hengjiu_claim_submitted_desc'),
            duration: 8,
          });
        }

        // REQ-UI-006: 신규 회사는 로그인 계정이 아직 없다 — 계정 발급 경로를 분명히 알린다.
        //
        // [2026-08-16] 병원까지 확대. 기공소 생성(제조사)에만 안내가 있어, 기공소가 병원을
        // 등록하면 계정이 생긴 줄 알고 넘어갔다 — 운영 병원 거래처 11건이 전부 계정 0건이었다
        // (2026-08-15 文圣区珊丹口腔诊所 확인). 회사 생성과 계정 생성은 별개 단계이며 기본 메뉴는
        // 계정 생성 시점에 부여되므로, 계정이 없으면 메뉴도 없다.
        //
        // 문구도 함께 정정한다 — 종전 "운영자에게 요청"은 2026-08-03 계정 관리 개방(담당 거래처
        // 전체) 이전 기준이라 낡았다. 생성자는 자기 담당 거래처 계정을 직접 발급할 수 있다
        // (CompanyList.canManageAccounts, 서버는 assertParentCompanyScope 로 멤버십 강제).
        if (!this.isEdit && !this.isOperator) {
          notification.info({
            message: this.$t('company_created_account_notice_title'),
            description: this.$t('company_created_account_notice_desc'),
            duration: 8,
          });
        }

        // @MX:NOTE: saved 페이로드(name/record)는 위저드 Step1 자동선택용. 기존 소비처(CompanyList.onSaved)는 인자 무시하므로 하위호환.
        this.$emit('saved', { name: this.formData.name, record: createdRecord });
        this.$emit('close');
      } catch (err) {
        // SPEC-CHINA-COMPANY-CREATE-CORE-001 REQ-CC-020(ii): 403/429는 이 모달에서 국소 처리.
        // 전역 extractApiError는 403을 하드코딩 문구로 덮어쓰고 429 분기가 없어 여기서 직접 분기한다.
        // (전역 helper 자체는 변경하지 않는다 — 비목표)
        const status = err?.response?.status;
        const rawMessage = err?.response?.data?.message || '';
        const provToken = extractErrorCodeToken(rawMessage);
        if (status === 400 && this.outboundProvenanceCode && provToken && provToken.startsWith('OP-PROV')) {
          // SPEC-CHINA-COMPANY-OUTBOUND-PICKER-001 REQ-OP-033: OP-PROV-001/002 저장 실패 복구 안내
          this.errorMsg = `出货记录关联已失效，请点击"清除"后重新保存 — ${rawMessage}`;
        } else if (status === 403) {
          // 서버가 내려준 denyMessage(REQ-CC-025 계약)를 우선 표시
          this.errorMsg = err?.response?.data?.message || this.$t('company_error_forbidden_fallback');
        } else if (status === 429) {
          this.errorMsg = this.$t('company_error_too_many_requests');
        } else {
          const apiErr = extractApiError(err);
          this.errorMsg = apiErr.message || this.$t('company_error_save_failed');
        }
      } finally {
        this.submitting = false;
      }
    },

    /**
     * 출고처 선택 → 회사명만 채우고 칩 부착 (REQ-OP-031). 타 필드 불가침.
     */
    onOutboundSelect({ customerCode, customerName }) {
      this.formData.name = customerName;
      this.outboundProvenanceCode = customerCode;
    },

    /** 清除 — 칩·provenance만 제거, 회사명은 그대로 둔다 (REQ-OP-031) */
    clearOutboundProvenance() {
      this.outboundProvenanceCode = null;
    },

    /**
     * REQ-OP-039: create 성공(201) 응답이 sourceOutboundCustomerCode를 에코하는지 검증.
     * 미부재·응답 레코드 자체 취득 실패(null shape, 판단 36) 모두 경고 — 검출 편향을 의도적으로 선택.
     * 모달이 닫힌 뒤에도 남는 전역 비차단 알림으로 표시(REQ-OP-039 표면 리터럴).
     */
    verifyOutboundProvenanceEcho(sentCode, record) {
      const echoedCode = record?.sourceOutboundCustomerCode;
      if (echoedCode === sentCode) return;
      notification.warning({
        message: '提示',
        description: '公司已创建，但出货记录关联未保存',
        duration: 0,
      });
    },

    /**
     * 헝지우 대리등록 — SPEC-CHINA-MFR-LAB-WRITE-HENGJIU-PROXY-001 REQ-HP-002/002c.
     *
     * 종전에는 여기서 credential 을 즉시 ACTIVE 로 만들었지만, 이제 **PENDING 클레임**만 낸다.
     * 활성화 판정은 헝지우 서버의 실제 RSA 서명(웹훅)이 한다 — 대리자는 소유자가 아니다.
     * 즉시 ACTIVE 경로를 남겨두면 서명 없이 매핑이 확정돼 SELF-ONBOARD-001 의 전제가 무너진다.
     *
     * @param {string} targetCompanyId 대상 기공소 _id. 대리 경로는 **필수** — 서버가 actor.sub 로
     *   폴백하지 않으므로(감사 A-1) 생성 직후엔 생성 응답의 _id 를 넘겨야 한다.
     * @returns {'skipped'|'claimed'} 클레임 생성 여부 — 호출부가 안내 문구를 고르는 데 쓴다.
     */
    async persistHengjiuCredential(targetCompanyId) {
      if (this.type !== 'DENTAL_LABORATORY') return 'skipped';
      if (!this.formData.hengjiuEnabled) return 'skipped';

      const factoryId = (this.formData.hengjiuFactoryId || '').trim();

      // REQ-HP-001: 헝지우 입력은 항상 선택 — 비어 있으면 조용히 건너뛴다.
      // v0.3.0: 공개키는 벤더 공통(서버 보유)이라 입력 항목이 factoryId 하나뿐이다.
      if (!factoryId) return 'skipped';

      if (!targetCompanyId) {
        // 생성 응답에서 _id 를 못 얻은 경우 — 회사는 이미 만들어졌으므로 수정 화면에서 재시도하게 안내
        throw new Error(this.$t('company_error_hengjiu_target_missing'));
      }

      await createOnboardingClaim({ factoryId, companyId: targetCompanyId });
      return 'claimed';
    },

    /**
     * payload 구성 — REQ-UNW-002: 금지 필드 미전송
     * create: viewManufacturerIds/viewDentalLabIds/role/deleteYn/_id 제외 (백엔드가 self-주입)
     * update: 운영자(XINLAIAN)는 authz 필드 포함 가능
     */
    buildPayload() {
      const base = {
        name: this.formData.name,
        ceo: this.formData.ceo,
        cNo: this.formData.cNo,
        businessLicenseImageUrl: this.formData.businessLicenseImageUrl,
        addrOne: this.formData.addrOne,
        addrTwo: this.formData.addrTwo,
        addrThree: this.formData.addrThree,
        addrDetail: this.formData.addrDetail,
        medicalDept: this.formData.medicalDept,
        registrationStatus: this.formData.registrationStatus,
        isTestCompany: this.formData.isTestCompany,
        call: this.formData.call,
      };

      // 헝지우 종속: 토글 ON이면 factoryId 전달.
      // 토글 OFF 시엔 회사 doc의 hengjiuFactoryId는 그대로 두고
      // factory_credentials.active=false 비활성화만 수행 (이력 보존).
      if (
        this.type === 'DENTAL_LABORATORY' &&
        this.formData.hengjiuEnabled &&
        this.formData.hengjiuFactoryId
      ) {
        base.hengjiuFactoryId = this.formData.hengjiuFactoryId.trim();
      }

      // 운영자(XINLAIAN)이고 수정 모드일 때만 authz 필드 포함 (REQ-FORM-003)
      if (this.isOperator && this.isEdit) {
        base.viewManufacturerIds = this.formData.viewManufacturerIds;
        if (this.type === 'HOSPITAL') {
          base.viewDentalLabIds = this.formData.viewDentalLabIds;
        }
      }

      // 출고처 provenance — create-only(REQ-OP-023: UPDATE_WHITELIST 미등재), 서버가 서버파생 dealerCode를 결정
      if (!this.isEdit && this.outboundProvenanceCode) {
        base.sourceOutboundCustomerCode = this.outboundProvenanceCode;
      }

      // 빈 문자열 필드 제거 (선택적 필드는 undefined로 보내지 않음)
      return Object.fromEntries(
        Object.entries(base).filter(([, v]) => v !== '' && v !== undefined),
      );
    },
  },
};
</script>

<style scoped>
/* 스크롤 영역 — 하단 버튼바(.cfm-fixed-bottom)를 제외한 폼 본문만 내부 스크롤 */
/* 260px가 아니라 200px를 빼는 이유: 실측상 모달 상하로 각 68px(총 135px)가 그냥 비어 있었다.
   200px = 모달 헤더(60) + 하단 버튼바(73) + 상하 여백(약 37씩). 뷰포트 833px 기준 가시 영역이
   573px → 633px로 늘어 한 화면에 필드 한 쌍이 더 들어온다. */
.cfm-scroll-area {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  padding: 24px 30px 16px;
}
/* 필드 간격 압축 — antd 기본 24px는 13~17개가 쌓이는 이 폼에서 300px 이상을 소모한다.
   16px로 줄여도 라벨-입력 그룹 간 시각적 분리는 충분하다(2열 페어의 gutter 16px와도 일치). */
.cfm-scroll-area :deep(.ant-form-item) {
  margin-bottom: 16px;
}
/* 하단 고정 영역 — 스크롤 영역 밖이라 항상 노출됨(sticky) */
.cfm-fixed-bottom {
  padding: 12px 30px 20px;
  border-top: 1px solid #f0f0f0;
}
/* 섹션 헤더 — 좌측 accent bar + 텍스트로 a-divider보다 수직 공간을 절약 */
.cfm-section-header {
  display: flex;
  align-items: center;
  height: 18px;
  margin: 20px 0 12px;
  padding-left: 8px;
  border-left: 3px solid #0e6e70;
  font-size: 13px;
  font-weight: 600;
  color: #333;
}
.cfm-section-header--first {
  margin-top: 0;
}
/* switch 필드를 label-above 대신 한 줄에 배치(label과 control이 같은 행) */
.cfm-inline-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.cfm-inline-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
}
.cfm-inline-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #888;
}
.business-license-widget {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.business-license-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.business-license-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #888;
}
.business-license-ocr-area {
  margin-top: 8px;
}
.business-license-ocr-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #888;
}
.business-license-ocr-retry {
  font-size: 12px;
  cursor: pointer;
  color: #0e6e70;
  text-decoration: underline;
}
.business-license-ocr-proposal {
  margin-top: 8px;
  padding: 12px;
  border: 1px solid #d9f0f0;
  border-radius: 6px;
  background: #f3fbfb;
}
.business-license-ocr-proposal__title {
  font-size: 12px;
  font-weight: 600;
  color: #0e6e70;
  margin-bottom: 8px;
}
.business-license-ocr-field {
  margin-bottom: 6px;
}
.business-license-ocr-field__label {
  font-size: 13px;
  color: #333;
  margin-right: 6px;
}
.business-license-ocr-field__value {
  font-size: 13px;
  color: #555;
}
.business-license-ocr-proposal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
.outbound-provenance-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 11.5px;
  color: #0e6e70;
}
.outbound-provenance-chip a {
  cursor: pointer;
  text-decoration: underline;
  color: #0e6e70;
}
/* authz 필드(조회 허용 제조사/기공소) 읽기전용 행 — 태그 나열 + [변경] 버튼 */
.authz-readonly-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.authz-readonly-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.authz-loading-text {
  font-size: 12px;
  color: #888;
}
/* 댕글링 참조(옵션 목록에 없는 id) — 문제로 읽히도록 경고색 처리 */
.authz-unknown-tag {
  color: #ad6800;
  background: #fffbe6;
  border-color: #ffe58f;
}

/* [2026-08-05] 전역 ant-design.scss 가 필수 별표를 앱 전체에서 숨긴다
   (.ant-form-item-required::before { display:none !important }).
   기공소/병원 추가·수정 모달에서는 필수 입력(회사명 등)을 사용자가 알 수 있어야 하므로
   이 폼에 한정해 되살린다. */
.cfm-show-required :deep(.ant-form-item-required::before) {
  display: inline-block !important;
}
</style>
